import {
	codecDecode,
	codecEncode,
	config,
	loadCodecs,
	setConfig,
} from "@/shared/index";
import { VortexConfig, VortexInitConfig, VortexDB } from "@/types";
import { VortexFrame } from "@/controller/frame";
import { MessageW2C } from "@/worker";
import { openDB, IDBPDatabase } from "idb";
import {
	VortexEvents,
	VortexGlobalDownloadEvent,
	VortexGlobalEvent,
	VortexGlobalEvents,
} from "@client/events";

export class VortexController extends EventTarget {
	private db: IDBPDatabase<VortexDB>;

	constructor(config: Partial<VortexInitConfig>) {
		super();
		// sane ish defaults
		const defaultConfig: VortexInitConfig = {
			// wisp: "/wisp/",
			prefix: "/vortex/",
			globals: {
				wrapfn: "$vortex$wrap",
				wrappropertybase: "$vortex__",
				wrappropertyfn: "$vortex$prop",
				cleanrestfn: "$vortex$clean",
				importfn: "$vortex$import",
				rewritefn: "$vortex$rewrite",
				metafn: "$vortex$meta",
				setrealmfn: "$vortex$setrealm",
				pushsourcemapfn: "$vortex$pushsourcemap",
				trysetfn: "$vortex$tryset",
				templocid: "$vortex$temploc",
				tempunusedid: "$vortex$tempunused",
			},
			files: {
				wasm: "/vortex.wasm.wasm",
				all: "/vortex.all.js",
				sync: "/vortex.sync.js",
			},
			flags: {
				serviceworkers: false,
				syncxhr: false,
				strictRewrites: true,
				rewriterLogs: false,
				captureErrors: true,
				cleanErrors: false,
				scramitize: false,
				sourcemaps: true,
				destructureRewrites: false,
				interceptDownloads: false,
				allowInvalidJs: true,
				allowFailedIntercepts: true,
			},
			siteFlags: {},
			codec: {
				encode: (url: string) => {
					if (!url) return url;

					return encodeURIComponent(url);
				},
				decode: (url: string) => {
					if (!url) return url;

					return decodeURIComponent(url);
				},
			},
		};

		const deepMerge = (target: any, source: any): any => {
			for (const key in source) {
				if (source[key] instanceof Object && key in target) {
					Object.assign(source[key], deepMerge(target[key], source[key]));
				}
			}

			return Object.assign(target || {}, source);
		};

		const newConfig = deepMerge(defaultConfig, config);
		newConfig.codec.encode = newConfig.codec.encode.toString();
		newConfig.codec.decode = newConfig.codec.decode.toString();
		setConfig(newConfig as VortexConfig);
	}

	async init(): Promise<void> {
		loadCodecs();

		await this.openIDB();
		navigator.serviceWorker.controller?.postMessage({
			vortex$type: "loadConfig",
			config,
		});
		dbg.log("config loaded");

		navigator.serviceWorker.addEventListener("message", (e) => {
			if (!("vortex$type" in e.data)) return;
			const data: MessageW2C = e.data;

			if (data.vortex$type === "download") {
				this.dispatchEvent(new VortexGlobalDownloadEvent(data.download));
			}
		});
	}

	createFrame(frame?: HTMLIFrameElement): VortexFrame {
		if (!frame) {
			frame = document.createElement("iframe");
		}

		return new VortexFrame(this, frame);
	}

	encodeUrl(url: string | URL): string {
		if (typeof url === "string") url = new URL(url);

		if (url.protocol != "http:" && url.protocol != "https:") {
			return url.href;
		}

		const encodedHash = codecEncode(url.hash.slice(1));
		const realHash = encodedHash ? "#" + encodedHash : "";
		url.hash = "";

		return config.prefix + codecEncode(url.href) + realHash;
	}

	decodeUrl(url: string | URL) {
		if (url instanceof URL) url = url.toString();
		const prefixed = location.origin + config.prefix;

		return codecDecode(url.slice(prefixed.length));
	}

	async openIDB(): Promise<IDBPDatabase<VortexDB>> {
		const db = await openDB<VortexDB>("$vortex", 1, {
			upgrade(db) {
				if (!db.objectStoreNames.contains("config")) {
					db.createObjectStore("config");
				}
				if (!db.objectStoreNames.contains("cookies")) {
					db.createObjectStore("cookies");
				}
				if (!db.objectStoreNames.contains("redirectTrackers")) {
					db.createObjectStore("redirectTrackers");
				}
				if (!db.objectStoreNames.contains("referrerPolicies")) {
					db.createObjectStore("referrerPolicies");
				}
				if (!db.objectStoreNames.contains("publicSuffixList")) {
					db.createObjectStore("publicSuffixList");
				}
			},
		});

		this.db = db;
		await this.#saveConfig();
		return db;
	}

	async #saveConfig() {
		if (!this.db) {
			console.error("Store not ready!");

			return;
		}
		await this.db.put("config", config, "config");
	}

	async modifyConfig(newconfig: Partial<VortexInitConfig>) {
		setConfig(Object.assign({}, config, newconfig));
		loadCodecs();

		await this.#saveConfig();
		navigator.serviceWorker.controller?.postMessage({
			vortex$type: "loadConfig",
			config,
		});
	}

	addEventListener<K extends keyof VortexGlobalEvents>(
		type: K,
		listener: (event: VortexGlobalEvents[K]) => void,
		options?: boolean | AddEventListenerOptions
	): void {
		super.addEventListener(type, listener as EventListener, options);
	}
}
